import { useNavigate, useLocation } from 'react-router-dom'
import { CONTACT_MAILTO } from '../lib/config'
import { useAuthStore } from '../stores/authStore'

interface SidebarProps {
  context?: 'default' | 'cv-generation'
}

const MAIN_NAV = [
  { label: 'Library', path: '/library', icon: 'folder_open' },
  { label: 'Generate CV', path: '/cv/generate', icon: 'description' },
  { label: 'CV History', path: '/cv/history', icon: 'history' },
]

export default function Sidebar({ context = 'default' }: SidebarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, signOut } = useAuthStore()

  const isActive = (path: string) => location.pathname.startsWith(path)

  const navItemClass = (path: string) =>
    [
      'w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors duration-200',
      isActive(path)
        ? 'border-l-2 border-primary-container bg-primary-container/5 text-primary-container font-medium'
        : 'text-secondary hover:text-on-surface hover:bg-surface-container-high',
    ].join(' ')

  return (
    <aside className="fixed left-0 top-0 h-dvh flex flex-col py-6 bg-surface-bright border-r border-outline-variant/20 w-16 lg:w-64 z-50 overflow-y-auto">
      {/* Wordmark */}
      <div className="px-4 lg:px-6 mb-10">
        <h1 className="hidden lg:block font-headline text-xl font-bold text-on-surface">
          Proof of Work
        </h1>
        <h1 className="lg:hidden text-center text-xl font-bold text-primary-container font-headline">
          P
        </h1>
        <p className="hidden lg:block font-label text-[10px] uppercase tracking-widest text-secondary mt-1 opacity-70">
          Your record of professional impact
        </p>
      </div>

      {/* Main nav */}
      <nav className="flex-1 space-y-0.5 px-2 lg:px-3">
        {MAIN_NAV.map((item) => (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className={navItemClass(item.path)}
          >
            <span className="material-symbols-outlined text-[20px] shrink-0">{item.icon}</span>
            <span className="hidden lg:inline font-label">{item.label}</span>
          </button>
        ))}
      </nav>

      {/* Footer */}
      <div className="space-y-0.5 px-2 lg:px-3 pt-4 border-t border-outline-variant/20">
        {context === 'cv-generation' && (
          <button
            onClick={() => navigate('/library')}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg text-secondary hover:text-on-surface hover:bg-surface-container-high transition-colors duration-200"
          >
            <span className="material-symbols-outlined text-[20px] shrink-0">arrow_back</span>
            <span className="hidden lg:inline font-label">Back to Library</span>
          </button>
        )}

        <button
          onClick={() => navigate('/settings')}
          className={navItemClass('/settings')}
        >
          <span className="material-symbols-outlined text-[20px] shrink-0">settings</span>
          <span className="hidden lg:inline font-label">Settings</span>
        </button>

        {user?.isAdmin && (
          <button
            onClick={() => navigate('/admin')}
            className={navItemClass('/admin')}
          >
            <span className="material-symbols-outlined text-[20px] shrink-0">admin_panel_settings</span>
            <span className="hidden lg:inline font-label">Admin</span>
          </button>
        )}

        <a
          href={CONTACT_MAILTO}
          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg text-secondary hover:text-on-surface hover:bg-surface-container-high transition-colors duration-200"
        >
          <span className="material-symbols-outlined text-[20px] shrink-0">help</span>
          <span className="hidden lg:inline font-label">Help</span>
        </a>

        <button
          onClick={signOut}
          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg text-secondary hover:text-on-surface hover:bg-surface-container-high transition-colors duration-200"
        >
          <span className="material-symbols-outlined text-[20px] shrink-0">logout</span>
          <span className="hidden lg:inline font-label">Log Out</span>
        </button>
      </div>
    </aside>
  )
}
